<template>
    <div class="project-brand-mgmt">
        <admin-page-header title="메일 브랜드(프로젝트) 관리">
            <template #actions>
                <b-button size="sm" variant="outline-secondary" :disabled="loading || savingAny" @click="reload">새로고침</b-button>
            </template>
        </admin-page-header>
        <!-- 강조·배지가 섞인 안내라 제목 영역 설명 대신 본문 첫 줄에 둔다 -->
        <p class="text-muted small mb-3">
            메일 브랜드를 <b>접속 주소가 아니라 프로젝트로</b> 판별합니다. 프로젝트별 브랜드를 지정하세요.<br>
            아래 목록에는 <b>전체 프로젝트</b>가 나옵니다.
            <b-badge variant="light" class="border text-muted font-weight-normal">미설정</b-badge>
            프로젝트는 잘못된 브랜드로 발송될 수 있으니, 화면에서 확인해 브랜드를 지정하세요.
        </p>

        <!-- 브랜드 목록을 못 받으면 고를 값이 없으므로 저장을 막고 다시 시도를 준다 -->
        <b-alert v-if="vocabError" show variant="danger" class="small d-flex align-items-center justify-content-between">
            <span>브랜드 목록을 불러오지 못했습니다({{ vocabError }}). 다시 불러올 때까지 저장할 수 없습니다.</span>
            <b-button size="sm" variant="outline-danger" class="ml-2 flex-shrink-0" :disabled="vocabLoading" @click="fetchVocabulary">다시 시도</b-button>
        </b-alert>

        <!-- 새 매핑 추가 / 갱신 -->
        <b-card class="mb-3" body-class="py-3">
            <b-form-row class="align-items-end">
                <b-col md="5">
                    <label class="small mb-1" for="brand-new-code">프로젝트 코드</label>
                    <b-form-input
                        id="brand-new-code"
                        v-model.trim="newCode"
                        list="brand-project-codes"
                        placeholder="예: HD_DOM_HQ_25"
                        size="sm"
                        autocomplete="off"
                        :state="newCodeState"
                    />
                    <b-form-datalist id="brand-project-codes" :options="projectCodes" />
                </b-col>
                <b-col md="4">
                    <label class="small mb-1" for="brand-new-brand">브랜드</label>
                    <b-form-select id="brand-new-brand" v-model="newBrand" :options="brandOptions" size="sm" />
                </b-col>
                <b-col md="3">
                    <b-button variant="primary" size="sm" block :disabled="!canSaveNew" @click="saveNew">
                        <b-spinner v-if="newRow && saving[newRow.projectCode]" small class="mr-1" />
                        추가 / 저장
                    </b-button>
                </b-col>
            </b-form-row>
            <div v-if="newCodeState === false" class="small text-danger mt-1">
                목록에 없는 프로젝트 코드입니다. 오타인지 확인하세요(방금 만든 프로젝트면 새로고침 후 다시 시도).
            </div>
            <div class="small text-muted mt-1">
                목록에서 찾기 어려운 프로젝트를 코드로 바로 지정합니다. 아래 표의 행에서 지정해도 같습니다.
            </div>
        </b-card>

        <!-- 목록 필터 -->
        <b-form-row class="mb-2 align-items-center">
            <b-col md="5" class="mb-1 mb-md-0">
                <b-form-input v-model.trim="filterText" size="sm" placeholder="프로젝트 코드 검색" aria-label="프로젝트 코드 검색" />
            </b-col>
            <b-col md="3" class="mb-1 mb-md-0">
                <b-form-checkbox v-model="unmappedOnly">미설정만 보기</b-form-checkbox>
            </b-col>
            <b-col md="4" class="small text-muted text-md-right">
                {{ visibleRows.length }}개 / 전체 {{ rows.length }}개
                <span v-if="dirtyRows.length" class="text-warning font-weight-bold"> · 저장 안 한 변경 {{ dirtyRows.length }}개</span>
            </b-col>
        </b-form-row>

        <!-- 매핑 목록 (백엔드가 준 최신순 정렬을 그대로 사용) -->
        <b-table
            :items="visibleRows"
            :fields="fields"
            :busy="loading"
            :per-page="PER_PAGE"
            :current-page="page"
            primary-key="projectCode"
            small
            hover
            responsive
            show-empty
        >
            <template #table-busy>
                <div class="text-center my-3"><b-spinner small /> 불러오는 중…</div>
            </template>
            <template #empty>
                <PageEmptyComponent
                    compact
                    :text="rows.length ? '조건에 맞는 프로젝트가 없습니다.' : '표시할 프로젝트가 없습니다.'"
                    :error="rowsError"
                    @retry="fetchRows"
                />
            </template>
            <template #cell(brand)="row">
                <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                    <b-form-select
                        v-model="row.item.selectedBrand"
                        :options="optionsFor(row.item.selectedBrand)"
                        size="sm"
                        style="max-width: 200px"
                        :disabled="!!saving[row.item.projectCode]"
                        :class="{ 'unmapped-select': !row.item.mapped && !isDirty(row.item) }"
                    />
                    <b-badge v-if="isDirty(row.item)" variant="warning">
                        변경됨 · {{ originalBrand(row.item) }} → {{ row.item.selectedBrand }}
                    </b-badge>
                    <b-badge
                        v-if="!row.item.mapped"
                        variant="light"
                        class="border text-muted font-weight-normal"
                    >
                        미설정 · 기본 {{ defaultBrand }}
                    </b-badge>
                </div>
            </template>
            <template #cell(updatedAt)="row">
                <span class="small text-muted">{{ formatTs(row.item.updatedAt) }}</span>
            </template>
            <template #cell(actions)="row">
                <b-button
                    size="sm"
                    :variant="isDirty(row.item) ? 'primary' : 'outline-primary'"
                    :disabled="!canSave || !brands.includes(row.item.selectedBrand) || !!saving[row.item.projectCode]"
                    @click="saveRow(row.item)"
                >
                    <b-spinner v-if="saving[row.item.projectCode]" small class="mr-1" />
                    저장
                </b-button>
            </template>
        </b-table>

        <div v-if="visibleRows.length > PER_PAGE" class="d-flex justify-content-center mt-2">
            <b-pagination v-model="page" :total-rows="visibleRows.length" :per-page="PER_PAGE" size="sm" />
        </div>

        <p class="small text-muted mt-2">
            지정 가능한 브랜드는 등록된 메일 템플릿의 sub_domain 값으로 한정됩니다
            <template v-if="vocabLoading">(불러오는 중…)</template>
            <template v-else-if="vocabError">(불러오지 못함)</template>
            <template v-else-if="!brands.length">(등록된 브랜드가 없습니다)</template>
            <template v-else>({{ brands.join(', ') }})</template>.
        </p>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';
import unsavedChangesGuard from '@shared_mixins/unsaved-changes-guard.js';

const PER_PAGE = 50;
// 백엔드는 프로젝트 코드를 대소문자 구분 없이 맞춘다(collation ci + casefold)
const fold = v => String(v || '').toLowerCase();

export default {
    name: 'ProjectBrandManagement',
    components: { PageEmptyComponent },
    // 저장 안 한 행 선택이 있으면 화면 이탈·새로고침 전에 확인
    mixins: [unsavedChangesGuard],
    data() {
        return {
            PER_PAGE,
            rows: [],
            rowsError: '',
            brands: [],
            vocabLoading: false,
            vocabError: '',
            loading: false,
            // projectCode → true (저장 중인 행). 연타로 PUT 이 겹치지 않게
            saving: {},
            newCode: '',
            newBrand: null,
            filterText: '',
            unmappedOnly: false,
            page: 1,
            // 정렬은 백엔드가 준 순서(프로젝트 id 내림차순=최신순)를 그대로 따르므로 sortable 미지정.
            fields: [
                { key: 'projectCode', label: '프로젝트 코드' },
                { key: 'brand', label: '브랜드' },
                { key: 'updatedAt', label: '수정 시각' },
                { key: 'actions', label: '', class: 'text-right' },
            ],
        };
    },
    computed: {
        // 미매핑 프로젝트에 화면상 미리 표시할 기본 브랜드(백엔드 발송 기본값과 같아야 한다).
        // 백엔드는 미매핑 시 brand=null 로만 내려주므로 기본값 표기는 프론트 책임이다.
        // qesg → saq 이름 변경(2026-10-20) 과도기라 브랜드 목록(DB)에 지금 있는 이름을 쓴다.
        defaultBrand() {
            return this.brands.includes('qesg') && !this.brands.includes('saq') ? 'qesg' : 'saq';
        },
        brandOptions() {
            return [
                { value: null, text: '브랜드 선택…', disabled: true },
                ...this.brands.map((b) => ({ value: b, text: b })),
            ];
        },
        // 브랜드 목록이 없거나 못 받았거나 다시 받는 중이면 저장을 막는다(서버는 목록 밖 값을 거절한다)
        canSave() {
            return !this.loading && !this.vocabLoading && !this.vocabError && this.brands.length > 0;
        },
        savingAny() {
            return Object.keys(this.saving).length > 0;
        },
        dirtyRows() {
            return this.rows.filter(this.isDirty);
        },
        hasUnsavedChanges() {
            return this.dirtyRows.length > 0;
        },
        visibleRows() {
            const q = fold(this.filterText);
            return this.rows.filter(r => (!this.unmappedOnly || !r.mapped) && (!q || fold(r.projectCode).includes(q)));
        },
        projectCodes() {
            return this.rows.map(r => r.projectCode);
        },
        newRow() {
            return this.newCode ? this.findRow(this.newCode) : null;
        },
        // 목록은 전체 프로젝트라 목록에 없는 코드는 오타다(매핑해도 어떤 메일에도 쓰이지 않음). 목록을 못 받았으면 판단하지 않는다.
        newCodeState() {
            if (!this.newCode || !this.rows.length) return null;
            return this.newRow ? null : false;
        },
        // 목록(전체 프로젝트)에 있는 코드만 — 목록을 못 받았으면 판단할 수 없으니 저장하지 않는다
        canSaveNew() {
            return !!this.newRow && this.brands.includes(this.newBrand) && this.canSave
                && !this.saving[this.newRow.projectCode];
        },
    },
    watch: {
        filterText() { this.page = 1; },
        unmappedOnly() { this.page = 1; },
        // '미설정만 보기' 중 저장하면 행이 빠져 페이지 수가 줄 수 있다 — 빈 페이지에 남지 않게
        'visibleRows.length'(n) {
            const last = Math.max(1, Math.ceil(n / PER_PAGE));
            if (this.page > last) this.page = last;
        },
    },
    async created() {
        // 행의 기본 브랜드가 브랜드 목록에 달려 있어 목록을 먼저 받는다
        await this.fetchVocabulary();
        this.fetchRows();
    },
    methods: {
        async reload() {
            if (this.dirtyRows.length) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '새로고침',
                    message: `저장하지 않은 변경 ${this.dirtyRows.length}개가 사라집니다. 새로고침할까요?`,
                });
                if (!ok) return;
            }
            await this.fetchVocabulary();
            await this.fetchRows();
        },
        // 백엔드가 ApiResponse 봉투({success, code, data})로 응답하면 payload 는 data.data.
        // 혹시 인터셉터가 이미 벗겼으면(raw) 그대로 쓴다.
        _payload(data) {
            if (data && typeof data === 'object' && !Array.isArray(data)
                && 'success' in data && 'data' in data) {
                return data.data;
            }
            return data;
        },
        async fetchVocabulary() {
            this.vocabLoading = true;
            this.vocabError = '';
            try {
                const { data } = await this.$axios.get('/admin/project-brands/vocabulary');
                const p = this._payload(data);
                this.brands = (p && p.brands) || [];
            } catch (e) {
                if (e.response?.status === 401) return;
                this.vocabError = getErrorMessage(e, '알 수 없는 오류');
            } finally {
                this.vocabLoading = false;
            }
        },
        toRow(r) {
            // 신 백엔드는 mapped 를 명시(미매핑=false, brand=null)한다.
            // 구 백엔드(mapped 필드 없음, 매핑된 항목만 반환)는 brand 존재로 매핑 여부를
            // 추론해 기존 동작을 유지한다(하위호환).
            const isMapped = typeof r.mapped === 'boolean' ? r.mapped : !!r.brand;
            return {
                ...r,
                mapped: isMapped,
                // 미설정 행은 기본 브랜드를 미리 표시(운영자가 그대로/변경하여 등록).
                selectedBrand: r.brand || this.defaultBrand,
            };
        },
        async fetchRows() {
            this.loading = true;
            this.rowsError = '';
            try {
                const { data } = await this.$axios.get('/admin/project-brands');
                const p = this._payload(data);
                const arr = Array.isArray(p) ? p : [];
                this.rows = arr.map(this.toRow);
            } catch (e) {
                if (e.response?.status === 401) return;
                const msg = getErrorMessage(e, '매핑 목록을 불러오지 못했습니다.');
                // 처음부터 실패면 표 자리에 오류 + 다시 시도, 이미 목록이 있으면 그대로 두고 알림만
                if (this.rows.length) this.toastErr(msg);
                else this.rowsError = msg;
            } finally {
                this.loading = false;
            }
        },
        originalBrand(row) {
            return row.brand || this.defaultBrand;
        },
        isDirty(row) {
            return row.selectedBrand !== this.originalBrand(row);
        },
        // 저장된 브랜드가 지금 어휘에 없으면 select 가 빈칸이 되므로 그 값을 선택 불가 옵션으로 붙여 보이게 한다
        optionsFor(value) {
            if (!value || this.brands.includes(value)) return this.brandOptions;
            const text = this.vocabLoading || this.vocabError ? value : `${value} (목록에 없음)`;
            return [...this.brandOptions, { value, text, disabled: true }];
        },
        findRow(code) {
            const key = fold(code);
            return this.rows.find(r => fold(r.projectCode) === key) || null;
        },
        saveRow(row) {
            return this.saveMapping(row.projectCode, row.selectedBrand, true);
        },
        async saveNew() {
            const row = this.newRow;
            if (!row) return;
            if (await this.saveMapping(row.projectCode, this.newBrand, false)) {
                this.newCode = '';
                this.newBrand = null;
            }
        },
        async saveMapping(code, brand, fromRow) {
            const c = (code || '').trim();
            if (!c) { this.toastWarn('프로젝트 코드를 입력하세요.'); return false; }
            if (!brand) { this.toastWarn('브랜드를 선택하세요.'); return false; }
            if (this.saving[c]) return false;
            const row = this.findRow(c);
            const before = row && row.mapped ? row.brand : null;
            this.$set(this.saving, c, true);
            try {
                const { data } = await this.$axios.put(`/admin/project-brands/${encodeURIComponent(c)}`, { brand });
                const saved = this._payload(data);
                // 저장한 행만 응답(갱신된 매핑)으로 바꾼다 — 다른 행의 저장 안 한 선택은 그대로 둔다.
                // 그 사이 목록을 다시 받았을 수 있으니 지금 목록에서 코드로 찾는다.
                const cur = this.findRow(c);
                if (cur) {
                    const next = saved && typeof saved === 'object' ? { ...cur, ...saved } : { ...cur, brand, mapped: true };
                    this.rows.splice(this.rows.indexOf(cur), 1, this.toRow(next));
                }
                this.toastOk(before && before !== brand ? `${c}: ${before} → ${brand} 저장됨` : `${c} → ${brand} 저장됨`);
                return true;
            } catch (e) {
                if (e.response?.status === 401) return false;
                this.toastErr(getErrorMessage(e, '저장에 실패했습니다.'));
                // 실패한 행만 원래 값으로
                const cur = this.findRow(c);
                if (fromRow && cur) cur.selectedBrand = this.originalBrand(cur);
                return false;
            } finally {
                this.$delete(this.saving, c);
            }
        },
        formatTs(ts) {
            if (!ts) return '-';
            try {
                return new Date(ts).toLocaleString('ko-KR');
            } catch (e) {
                return ts;
            }
        },
        toastOk(msg) {
            this.$EventBus.$emit('toast-success', '완료', msg);
        },
        toastWarn(msg) {
            this.$EventBus.$emit('toast-warning', '확인', msg);
        },
        toastErr(msg) {
            this.$EventBus.$emit('toast-failure', '오류', msg);
        },
    },
};
</script>

<style scoped>
.project-brand-mgmt {
    max-width: 900px;
}

/* 미설정(미매핑) 행의 드롭다운은 회색으로 표시해 실제 지정값과 구분한다. */
.unmapped-select {
    color: #6c757d;
    font-style: italic;
}
</style>
